// The numbers behind the search page summary: how many searches, when each
// sample runs, the report name, and the data dots on keyword tags.

export const SAMPLE_COUNTS = [1, 3, 5, 10, 15];

export const SAMPLE_SPACINGS = [
  { id: "minute", label: "Min", ms: 60 * 1000 },
  { id: "15min", label: "15m", ms: 15 * 60 * 1000 },
  { id: "hour", label: "Hour", ms: 60 * 60 * 1000 },
  { id: "day", label: "Day", ms: 24 * 60 * 60 * 1000 },
];

// Every keyword x postcode is searched on both.
export const DEVICES = ["mobile", "desktop"];

// Measured 2026-10-13: about 4-10 seconds per search.
export const SECONDS_PER_SEARCH = 8;

// With one sample a minute, more searches than this per sample may not
// finish before the next sample is due (Scott, 3 Nov 2026: above 4).
export const MINUTE_SPACING_WARNING_ABOVE = 4;

export const MINUTE_WARNING =
  "This search is too large to finish each minute. Samples will run at the same time, and Minute spacing repeats the same search close together.";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function spacingMs(spacingId) {
  const spacing = SAMPLE_SPACINGS.find(s => s.id === spacingId);
  return spacing ? spacing.ms : 0;
}

// keywords x postcodes x devices
export function searchesPerSample(keywordCount, postcodeCount) {
  return keywordCount * postcodeCount * DEVICES.length;
}

export function totalSearches(keywordCount, postcodeCount, samples) {
  return searchesPerSample(keywordCount, postcodeCount) * samples;
}

// now, now + spacing, now + 2 x spacing ... (as Dates)
export function plannedSampleTimes(now, samples, spacingId) {
  const gap = spacingMs(spacingId);
  return Array.from({ length: samples }, (_, i) => new Date(now.getTime() + i * gap));
}

// When the last sample should be done, using SECONDS_PER_SEARCH.
export function estimatedFinish(now, samples, spacingId, perSample) {
  const times = plannedSampleTimes(now, samples, spacingId);
  const last = times[times.length - 1] || now;
  return new Date(last.getTime() + perSample * SECONDS_PER_SEARCH * 1000);
}

export function showMinuteWarning(spacingId, perSample) {
  return spacingId === "minute" && perSample > MINUTE_SPACING_WARNING_ABOVE;
}

// The business locations a scan's radius is centred on: the Your business
// postcode first, then the other sites - each once it has been found on
// postcodes.io, and only once. Each: { postcode, lat, lng, places, isMain }.
// The postcodes searched come from these (core/locations/radius.js).
export function businessLocations(business, sites) {
  const list = [];
  const code = business && business.lat != null && business.lng != null ? business.postcode.trim() : "";
  if (code) list.push({ postcode: code, lat: business.lat, lng: business.lng, places: business.places || [], isMain: true });
  for (const site of sites || []) {
    const p = site.place;
    if (!p || p.lat == null || p.lng == null || list.some(x => x.postcode === p.postcode)) continue;
    list.push({ postcode: p.postcode, lat: p.lat, lng: p.lng, places: p.places || [], isMain: false });
  }
  return list;
}

// Over the account's postcode limit (agencies.max_postcodes_per_scan)?
export function overPostcodeLimit(postcodeCount, max) {
  return postcodeCount > max;
}

// "S1 +4": the first postcode, plus how many more.
export function postcodeSummary(postcodes) {
  if (!postcodes || postcodes.length === 0) return "";
  return postcodes.length > 1 ? `${postcodes[0]} +${postcodes.length - 1}` : postcodes[0];
}

// "8 Oct 2026" (UK date)
export function shortDate(date) {
  const parts = ukParts(date);
  return `${parts.day} ${MONTHS[parts.month - 1]} ${parts.year}`;
}

// "Plumbing · S1 +4 · 8 Oct 2026"
export function reportName(nicheName, postcodes, date) {
  return [nicheName, postcodeSummary(postcodes), shortDate(date)].filter(Boolean).join(" · ");
}

// "10:15" (UK time)
export function shortTime(date) {
  const parts = ukParts(date);
  return `${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}`;
}

// "10:15 · 11:15 · 12:15" - long lists are cut short: "10:15 · 10:30 · 10:45 … +12".
// Daily samples: "10:15 daily from today".
export function sampleTimesText(times, spacingId, shown = 3) {
  if (times.length === 0) return "";
  if (spacingId === "day" && times.length > 1) return `${shortTime(times[0])} daily from today`;
  const text = times.slice(0, shown).map(shortTime).join(" · ");
  return times.length > shown ? `${text} … +${times.length - shown}` : text;
}

// "12:20", or "15 Oct 12:20" when it isn't today.
export function finishText(now, finish) {
  return shortDate(now) === shortDate(finish)
    ? shortTime(finish)
    : `${shortDate(finish).replace(/ \d{4}$/, "")} ${shortTime(finish)}`;
}

// The town most of the postcodes are in, for the map header.
export function mainArea(postcodes) {
  const counts = {};
  for (const p of postcodes || []) {
    for (const place of p.places || []) counts[place] = (counts[place] || 0) + 1;
  }
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return best ? best[0] : "";
}

// Dot 1 on a keyword tag: how many of your past scans we hold.
// "full" = 3 or more, "half" = 1-2, "empty" = never.
export function dataDotLevel(jobCount) {
  if (jobCount >= 3) return "full";
  if (jobCount >= 1) return "half";
  return "empty";
}

// Hover text for dot 1: "You've scanned this 4 times, last 2 Oct".
export function dataDotDetail(history) {
  if (!history || history.jobCount === 0) return "You haven't scanned this keyword before";
  const times = history.jobCount === 1 ? "once" : `${history.jobCount} times`;
  const parts = history.lastAt ? ukParts(new Date(history.lastAt)) : null;
  const last = parts ? `, last ${parts.day} ${MONTHS[parts.month - 1]}` : "";
  return `You've scanned this ${times}${last}`;
}

// Past keyword rows -> history per standard form (any location).
// rows: [{ standard_form, job_id, jobs: { started_at } }]
// Returns { "emergency plumber": { jobCount, lastAt } }.
export function summariseKeywordHistory(rows) {
  const byForm = {};
  for (const row of rows || []) {
    const entry = byForm[row.standard_form] || (byForm[row.standard_form] = { jobIds: new Set(), lastAt: null });
    entry.jobIds.add(row.job_id);
    const at = row.jobs?.started_at;
    if (at && (!entry.lastAt || at > entry.lastAt)) entry.lastAt = at;
  }
  const out = {};
  for (const [form, entry] of Object.entries(byForm)) {
    out[form] = { jobCount: entry.jobIds.size, lastAt: entry.lastAt };
  }
  return out;
}

// What the Worker's /start-scan expects.
// keywords: [{ raw, standard, subGroupId }]; postcodes: [{ postcode, lat, lng }];
// niche / subGroup: { id } or null; business: { name, website, postcode, lat, lng }.
export function buildScanRequest({ name, keywords, postcodes, niche, subGroup, suggestionId, business, samples, spacing }) {
  return {
    name,
    keywords: keywords.map(k => ({ raw_text: k.raw, standard_form: k.standard || k.raw, sub_group_id: k.subGroupId || null })),
    postcodes: postcodes.map(p => ({ postcode: p.postcode, lat: p.lat ?? null, lng: p.lng ?? null })),
    niche_id: niche ? niche.id : null,
    sub_group_id: subGroup ? subGroup.id : null,
    niche_suggestion_id: suggestionId || null,
    business_name: business.name.trim() || null,
    business_website: business.website.trim() || null,
    business_postcode: business.postcode.trim() || null,
    business_lat: business.lat ?? null,
    business_lng: business.lng ?? null,
    samples_per_run: samples,
    sample_spacing: spacing,
  };
}

const UK_CLOCK = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/London", year: "numeric", month: "numeric", day: "numeric",
  hour: "numeric", minute: "numeric", hourCycle: "h23",
});

function ukParts(date) {
  const parts = {};
  for (const { type, value } of UK_CLOCK.formatToParts(date)) parts[type] = parseInt(value, 10);
  return parts;
}
